import * as d3 from 'd3';
import { ZoomTransform, ZoomableChartController } from 'src/utils/helpers/BaseChartController';
import { GraphTooltipController } from './GraphTooltipController';
import { TubeSheetChartType, TubeSheetGraphData, TubeSheetGraphDataElement } from './types';
import { TubeStatus_t } from 'foreman-slink';
import { TUBE_RADIUS } from './constants';

const clickedClass = 'tube-sheet-circle-clicked';

/**
 * Handles drawing the svg using an imperative API.
 */
export class TubeSheetGraphController extends ZoomableChartController {
  protected readonly tooltip: GraphTooltipController;
  private getTooltipForItem: (item: TubeSheetGraphDataElement) => string[] = () => [];

  private xMin: number = 0;
  private yMin: number = 0;
  private xMax: number = 1;
  private yMax: number = 1;

  private get width() {
    return this.xMax - this.xMin;
  }

  private get height() {
    return this.yMax - this.yMin;
  }

  private get viewBox() {
    return {
      xMin: this.xMin - 1,
      yMin: this.yMin - 1,
      width: this.width + 2,
      height: this.height + 2,
      toString: function (this) {
        return `${this.xMin} ${this.yMin} ${this.width} ${this.height}`;
      },
    };
  }

  constructor(svg: SVGSVGElement) {
    super(svg);
    this.svg.attr('viewBox', this.viewBox.toString());
    this.svg.selectChildren().remove();

    // Initialize symbols group
    this.svg.append('g').attr('data-id', 'symbols-group-root');
    this.tooltip = new GraphTooltipController(svg);
    this.zoom.scaleExtent([0.5, 5]);
    this.resetZoom(true);
  }

  public readonly setData = (chartType: TubeSheetChartType, data: TubeSheetGraphData) => {
    const { segments } = data;
    this.xMin = d3.min(segments.flat(), d => d.x) ?? 0;
    this.xMax = d3.max(segments.flat(), d => d.x) ?? 0;
    this.yMin = d3.min(segments.flat(), d => d.y) ?? 0;
    this.yMax = d3.max(segments.flat(), d => d.y) ?? 0;

    const maxDim = Math.max(this.width, this.height);
    this.zoom.translateExtent([
      [this.xMin - maxDim * 0.5, this.yMin - maxDim * 0.5],
      [this.xMax + maxDim * 0.5, this.yMax + maxDim * 0.5],
    ]);

    this.render(chartType, data);
    this.tooltip.onParentLayoutChanged();
  };

  public reset() {
    this.resetZoom(true);
    this.svg.selectAll(`.${clickedClass}`).classed(clickedClass, false);
    this.tooltip.setVisible(false);
  }

  public readonly setTooltipAccessor = (accessor: (item: TubeSheetGraphDataElement) => string[]) => {
    this.getTooltipForItem = accessor;
  };

  private readonly render = (chartType: TubeSheetChartType, data: TubeSheetGraphData) => {
    const { segments, breakpoints } = data;
    const symbolGenerator = d3

      .symbol()
      .type(d =>
        d.status === TubeStatus_t.Blocked && chartType === TubeSheetChartType.MAIN ? d3.symbolX : d3.symbolCircle,
      );
    // Currently, only render the SymbolX on Tube's Completed Chart, otherwise use circle
    const symbolSize = () => {
      return Math.pow(TUBE_RADIUS * 0.9, 2) * Math.PI;
    };

    this.svg
      .attr('viewBox', this.viewBox.toString())
      .select('[data-id=symbols-group-root]')
      .selectAll('g')
      .data(segments)
      .join('g')
      .selectAll('.symbol')
      .data(d => d)
      .join('path')
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .attr('d', symbolGenerator.size(symbolSize))
      .attr('stroke-width', d => TUBE_RADIUS * 0.2)
      .attr('cursor', 'pointer')
      .classed('symbol', true)
      .classed('symbol-cleaned', data => data.status === TubeStatus_t.Cleaned)
      .classed('symbol-not-cleaned', data => data.status === TubeStatus_t.Uncleaned)
      .classed('symbol-blocked', data => data.status === TubeStatus_t.Blocked)
      .classed('symbol-skipped', data => data.status === TubeStatus_t.Skip)
      .classed('symbol-none', data => data.status === (TubeStatus_t.None || TubeStatus_t.Unknown))
      .classed('symbol-autostroke', data => data.autoStroke)
      .classed('tube-sheet-heat-breakpoint-avg', data => data.cleanTime >= breakpoints.duration.avg)
      .classed('tube-sheet-heat-breakpoint-warning', data => data.cleanTime >= breakpoints.duration.warning)
      .classed('tube-sheet-heat-breakpoint-danger', data => data.cleanTime >= breakpoints.duration.danger)
      .classed('tube-sheet-lance-breakpoint-avg', data => data.lanceSpeed >= breakpoints.lanceSpeed.avg)
      .classed('tube-sheet-lance-breakpoint-warning', data => data.lanceSpeed >= breakpoints.lanceSpeed.warning)
      .classed('tube-sheet-lance-breakpoint-danger', data => data.lanceSpeed >= breakpoints.lanceSpeed.danger)
      .classed('tube-sheet-depth-breakpoint-avg', data => data.depth >= breakpoints.depth.avg)
      .classed('tube-sheet-depth-breakpoint-warning', data => data.depth >= breakpoints.depth.warning)
      .classed('tube-sheet-depth-breakpoint-danger', data => data.depth >= breakpoints.depth.danger)
      .on('click', this.circleClick)
      .on('mouseover', this.selectionUpdate)
      .on('mousemove', this.selectionUpdate)
      .on('mouseout', this.selectionInvisible);
  };

  protected performZoomReset(transform: ZoomTransform, instant?: boolean | undefined): void | Promise<void> {
    this.svg.selectAll(`.${clickedClass}`).classed(clickedClass, false);
    this.selectionInvisible();

    this.svg
      .transition()
      .duration(instant ? 0 : 750)
      .call(transform, new d3.ZoomTransform(1, 0, 0));
  }

  protected performZoom(e: d3.D3ZoomEvent<SVGElement, any>): void | Promise<void> {
    this.svg.select('g').attr('transform', e.transform.toString());
  }

  private readonly setTextFromEvent = (event: MouseEvent, d: TubeSheetGraphDataElement) => {
    const [x, y] = d3.pointer(event, this.svg.node()!);
    const tooltips = this.getTooltipForItem(d);
    this.tooltip.setText(tooltips, x, y);
    this.tooltip.setVisible(true);
  };

  protected readonly selectionInvisible = () => {
    if (this.hasSelection()) return;
    this.tooltip.setVisible(false);
  };

  protected readonly selectionUpdate = (event: MouseEvent, d: TubeSheetGraphDataElement): void => {
    event.stopPropagation();
    if (this.hasSelection()) return;
    this.setTextFromEvent(event, d);
  };

  protected circleClick = (event: MouseEvent, d: TubeSheetGraphDataElement) => {
    event.stopPropagation();
    const clickedCircles = this.svg.selectAll(`.${clickedClass}`);
    const didClickSelectedElement = clickedCircles.nodes().some(n => n === event.currentTarget);
    clickedCircles.classed(clickedClass, false);
    if (!didClickSelectedElement) {
      d3.select(event.currentTarget! as Element).classed(clickedClass, true);
      this.setTextFromEvent(event, d);
    } else {
      this.tooltip.setVisible(false);
    }
  };

  private hasSelection = (): boolean => {
    return !!this.svg.selectAll(`.${clickedClass}`).node();
  };
}
